import { ChangeEvent, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiFilterItem,
  MiInternalDrawer,
  MiSearchInput,
} from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import { IAreaTypeResponse, IBidArea } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { mdiPlus } from "@mdi/js";
import { QuantityControl } from "@mibuildweb/components";
import { useAreaTypes } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { useCreateBidContext } from "../../../CreateBidContext";
import { Floor } from "../../../CreateBids.types";

export function AddAreaDrawer({
  open,
  onClose,
  selectedFloor,
  setPreviewAreas = () => {},
}: AddAreaDrawerProps) {
  const [search, setSearch] = useState("");
  const [areaToAdd, setAreaToAdd] = useState<IBidArea[]>([]);

  const debouncedSearch = useDebounce(search);
  const { classes, theme } = useStyles();
  const { areaTypes, getAreaTypes } = useAreaTypes();
  const { form } = useCreateBidContext();
  const { watch, setValue } = form;
  const { planAreas } = watch();

  useEffect(() => {
    if (open) {
      getAreaTypes({ search: debouncedSearch });
    } else {
      setAreaToAdd([]);
    }
  }, [open, debouncedSearch]);

  useEffect(() => {
    setPreviewAreas(areaToAdd);
  }, [areaToAdd]);

  const getItemCount = (item: IAreaTypeResponse) =>
    areaToAdd.filter((area) => area.areaTypeId === item.id).length;

  const getNextAreaLabelNumber = (item: IAreaTypeResponse) => {
    const numbers = [...planAreas, ...areaToAdd]
      .filter((area) => area.areaTypeId === item.id)
      .map((area) => Number(area.label?.replace(`${item.name} `, "")))
      .filter((num) => !Number.isNaN(num))
      .sort((a, b) => a - b);
    const firstGapIndex = numbers.findIndex((num, index) => num !== index + 1);
    return firstGapIndex === -1 ? numbers.length + 1 : firstGapIndex + 1;
  };

  const isSelected = (itemId: string) =>
    areaToAdd.some((a) => a.areaTypeId === itemId);

  const addNewArea = (item: IAreaTypeResponse) => {
    // Calculate next displayOrder for this floor+column
    const existingInColumn = planAreas.filter(
      (a) => a.floorId === selectedFloor?.id && a.specColumn === item.specColumn
    );
    const newInColumn = areaToAdd.filter(
      (a) => a.floorId === selectedFloor?.id && a.specColumn === item.specColumn
    );
    const nextOrder = existingInColumn.length + newInColumn.length;

    return {
      id: createUUID(),
      areaTypeId: item.id,
      label: `${item.name} ${getNextAreaLabelNumber(item)}`,
      floorId: selectedFloor?.id,
      specColumn: item.specColumn,
      areaTypeName: item.name,
      displayOrder: nextOrder,
    };
  };

  const handleIncreaseQty = (
    e: ChangeEvent<HTMLInputElement>,
    item: IAreaTypeResponse
  ) => {
    e.stopPropagation();
    const addedArea = [...areaToAdd, addNewArea(item)] as IBidArea[];
    setAreaToAdd(addedArea);
  };

  const handleDecreaseQty = (
    e: ChangeEvent<HTMLInputElement>,
    item: IAreaTypeResponse
  ) => {
    e.stopPropagation();
    const indexOfAreaToRemove = areaToAdd.findLastIndex(
      (area) => area.areaTypeId === item.id
    );
    if (indexOfAreaToRemove !== -1) {
      const updatedAreas = [...areaToAdd];
      updatedAreas.splice(indexOfAreaToRemove, 1);
      setAreaToAdd(updatedAreas);
    }
  };

  const handleEmptyAreaSelect = (
    selected: boolean,
    item: IAreaTypeResponse
  ) => {
    const updatedAreas = [...areaToAdd, addNewArea(item)].filter(
      (val) => selected || val.areaTypeId !== item.id
    ) as IBidArea[];
    setAreaToAdd(updatedAreas);
  };

  const handleAddAreaToPlan = () => {
    const updatedAreas = [...planAreas, ...areaToAdd];
    setValue("planAreas", updatedAreas, { shouldDirty: true });
    setAreaToAdd([]);
    onClose();
  };

  return (
    <MiInternalDrawer
      removeHeaderMargin
      open={open}
      onClose={onClose}
      title="Add Areas"
      width={250}
    >
      <Box className={classes.root}>
        <MiSearchInput
          style={{ margin: theme.spacing(0, 0, 1, 0) }}
          placeholder="Search Areas"
          handleChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        {areaTypes.map((item) => (
          <MiFilterItem
            key={item.id}
            keyVal={item.id}
            title={item.name}
            handleClick={() => {
              handleEmptyAreaSelect(!isSelected(item.id), item);
            }}
            selected={isSelected(item.id)}
            rightAction={
              isSelected(item.id) && (
                <Box className={classes.qtyControlBox}>
                  <Typography>QTY</Typography>
                  <QuantityControl
                    onIncreaseQty={(e: ChangeEvent<HTMLInputElement>) => {
                      handleIncreaseQty(e, item);
                    }}
                    onDecreaseQty={(e: ChangeEvent<HTMLInputElement>) => {
                      handleDecreaseQty(e, item);
                    }}
                    quantity={getItemCount(item)}
                  />
                </Box>
              )
            }
          />
        ))}
        <MiButton
          title={`Add Areas to ${selectedFloor?.name}`}
          startIcon={{ path: mdiPlus }}
          variant={BUTTON_VARIANTS.CONTAINED}
          sx={{ marginTop: theme.spacing(1) }}
          size="small"
          onClick={handleAddAreaToPlan}
        />
      </Box>
    </MiInternalDrawer>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
  },
  qtyControlBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
  },
}));

export interface AddAreaDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedFloor: Floor | null;
  setPreviewAreas?: (areas: IBidArea[]) => void;
}
